// @mui
import { Box, Container, Stack, Typography } from "@mui/material";
//
import { StyledRoot, StyledContent } from "./styles";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import KycStepper from "../../pages/auth/kyc/KycStepper";
import { createContext, useEffect, useState } from "react";
import fetcher from "../../api/fetcher";
import { END_POINTS } from "../../api/EndPoints";
import { businessDetails } from "../../api/types/response/kyc";
import { PATH_KYC } from "../../routes/paths";
import { useAuthContext } from "../../auth/useAuthContext";
import {
  getCurrentLocation,
  NavigationSteps,
  TabsData,
} from "../../utils/common";
import { BanksData } from "../../api/types/response/bank";
import useResponsive from "../../hooks/useResponsive";
import ShowLastUpdate from "../../components/CustomComponents.tsx/ShowLastUpdate";
import { XMLParser } from "fast-xml-parser";
import AadhaarAndPanDetailsPage from "../../pages/auth/kyc/AadhaarAndPanDetailsPage";

// ----------------------------------------------------------------------

export const BusinessDetailsContext = createContext(
  {} as businessDetails & {
    getBusinessDetail: () => void;
    service: { _id: string; serviceName: string; offlineKyc: boolean }[];
  },
);

export const BankContext = createContext(
  {} as {
    bankData: BanksData[];
    selectedBank: string;
    setSelectedBank: (bankId: string) => void;
  },
);

export default function KYCLayout() {
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const isDesktop = useResponsive("up", "md");
  const [businessDetail, setBusinessDetail] = useState({} as businessDetails);
  const [bankData, setBankData] = useState<BanksData[]>([]);
  const [sessionId, setSessionId] = useState("");
  const [isAadhaarDetailFetching, setIsAadhaarDetailFetching] = useState(false);
  const [isPanDetailsFetching, setIsPanDetailsFetching] = useState(false);
  const [aadhaarData, setAadhaarData] = useState({
    aadharNumber: "",
    nameInAadhaar: "",
    aadharDOB: "",
    aadharGender: "",
    distInAadhar: "",
    aadharPincode: "",
    aadharState: "",
    cityInAadhar: "",
    addressInAadhar: "",
    aadharCO: "",
    aadharCountry: "",
    aadharHouse: "", // not present in your data
    aadharPO: "",
    aadharStreet: "", // not present
    aadharSubDistrict: "", // not present
    aadharPhoto: "",
  });
  const [panData, setPanData] = useState({
    name: "",
    dob: "",
    pan: "",
  });
  const [selectedBank, setSelectedBank] = useState("");
  const [service, setService] = useState<
    {
      _id: string;
      serviceName: string;
      offlineKyc: boolean;
    }[]
  >([]);

  useEffect(() => {
    user.profileStatus > 1 && getBusinessDetail();
    getLocation();
    getServiceStatus();
  }, [pathname]);

  useEffect(() => {
    const requestedPathname =
      NavigationSteps.find((item) => item.status.includes(user.profileStatus))
        ?.url || "";
    if (requestedPathname !== pathname) {
      navigate(requestedPathname);
    }
  }, [user]);

  const getBusinessDetail = async () => {
    try {
      const Response = await fetcher.get(
        END_POINTS.KYC.GST.FETCH_BUSINESS_DETAIL,
      );
      setBusinessDetail(Response.data.data);
    } catch (error) {
      navigate(NavigationSteps[0].url);
    }
  };

  const getServiceStatus = async () => {
    try {
      const Response = await fetcher.get(END_POINTS.AUTH.SERVICE_INFO);
      setService(Response.data);
    } catch (error) {}
  };

  useEffect(() => {
    pathname.split("/").includes("bank") && getBankList();
  }, []);

  const getBankList = async () => {
    try {
      const Response = await fetcher.get(END_POINTS.KYC.BANK.FETCH("partner"));
      setBankData(
        Response.data.filter((item: BanksData) => item.bankType == "partner"),
      );
      setSelectedBank(Response.data[0]._id);
    } catch (error) {
      console.error(error);
    }
  };

  const getLocation = async () => {
    try {
      await getCurrentLocation();
    } catch (error) {
      // showToast.error("Please allow location to continue use AssuredPay.");
      // logout();
    }
  };

  useEffect(() => {
    if (user.profileStatus == 3) {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      if (sessionId) {
        setSessionId(sessionId);
        getAadhaarVerificationStatus(sessionId);
        getPanVerificationStatus(sessionId);
      }
    } else {
      const requestedPathname =
        NavigationSteps.find((item) => item.status.includes(user.profileStatus))
          ?.url || "";
      navigate(requestedPathname);
    }
  }, []);

  const getAadhaarVerificationStatus = async (id: string) => {
    try {
      setIsAadhaarDetailFetching(true);
      const Response = await fetcher.post(
        END_POINTS.KYC.AADHAAR.DIGILOCKER_VERIFICATION_STATUS(id),
        { documentType: "aadhaar" },
      );
      const parser = new XMLParser({
        ignoreAttributes: false, // keep attributes like uid, dob, gender
        attributeNamePrefix: "", // remove @ from keys
        allowBooleanAttributes: true,
      });
      const jsonData = parser.parse(Response.data.xml);

      const uidData = jsonData.Certificate.CertificateData.KycRes.UidData;

      const poi = uidData.Poi || {};
      const poa = uidData.Poa || {};
      const photo = uidData.Pht || "";

      // Construct address
      const address = [
        poa.co,
        poa.vtc,
        poa.po,
        poa.dist,
        poa.state,
        poa.pc ? `- ${poa.pc}` : "",
        poa.country,
      ]
        .filter(Boolean)
        .join(", ");

      setAadhaarData({
        aadharNumber: uidData.uid || "",
        nameInAadhaar: poi.name || "",
        aadharDOB: poi.dob || "",
        aadharGender: poi.gender || "",
        distInAadhar: poa.dist || "",
        aadharPincode: poa.pc || "",
        aadharState: poa.state || "",
        cityInAadhar: poa.vtc || "",
        addressInAadhar: address,
        aadharCO: poa.co || "",
        aadharCountry: poa.country || "",
        aadharHouse: poa.house || "", // not present in your data
        aadharPO: poa.po || "",
        aadharStreet: poa.street || "", // not present
        aadharSubDistrict: poa.subdist || "", // not present
        aadharPhoto: photo,
      });
    } catch (error) {
    } finally {
      setIsAadhaarDetailFetching(false);
    }
  };

  const getPanVerificationStatus = async (id: string) => {
    try {
      setIsPanDetailsFetching(true);
      const Response = await fetcher.post(
        END_POINTS.KYC.AADHAAR.DIGILOCKER_VERIFICATION_STATUS(id),
        {
          documentType: "pan",
        },
      );
      const parser = new XMLParser({
        ignoreAttributes: false, // keep attributes like uid, dob, gender
        attributeNamePrefix: "", // remove @ from keys
        allowBooleanAttributes: true,
      });
      const jsonData = parser.parse(Response.data.xml);

      setPanData({
        name: jsonData?.Certificate?.IssuedTo?.Person?.name,
        dob: jsonData?.Certificate?.IssuedTo?.Person?.dob,
        pan: jsonData?.Certificate?.number,
      });
    } catch (error) {
    } finally {
      setIsPanDetailsFetching(false);
    }
  };

  return (
    <StyledRoot>
      <Box
        sx={{
          bgcolor: "background.default",
          width: "100%",
          position: "fixed",
          zIndex: 10,
        }}
      >
        <Header />
      </Box>
      <StyledContent>
        <Container maxWidth={"xl"}>
          {sessionId ? (
            <Box>
              <AadhaarAndPanDetailsPage
                aadhaarData={aadhaarData}
                panData={panData}
                setSession={setSessionId}
                isAadhaarDetailFetching={isAadhaarDetailFetching}
                isPanDetailsFetching={isPanDetailsFetching}
              />
            </Box>
          ) : (
            <BusinessDetailsContext.Provider
              value={{ ...businessDetail, getBusinessDetail, service }}
            >
              {user.profileStatus < 8 && (
                <Stack flexDirection={"row"} justifyContent={"center"}>
                  <KycStepper />
                </Stack>
              )}
              <BankContext.Provider
                value={{ bankData, selectedBank, setSelectedBank }}
              >
                <Outlet />
              </BankContext.Provider>
            </BusinessDetailsContext.Provider>
          )}
        </Container>
      </StyledContent>

      {isDesktop && (
        <Box
          sx={{
            bgcolor: "background.default",
            width: "100%",
            position: "fixed",
            bottom: 0,
            zIndex: 9,
          }}
        >
          <Footer />
        </Box>
      )}
      <ShowLastUpdate />
    </StyledRoot>
  );
}
